import React, { useContext, useMemo, useState, useEffect } from "react";
import styled from "styled-components";
import { ListItem } from "./PositionHelpers";
import Select from "react-select";
import RosterContext from "../../../contexts/RosterContext";
import { AlertConstants } from "best-common-react";
import { useAlert } from "../../hooks/useAlert";
import RosterApi from "../../../httpClients/RosterApi";
import SelectedTeamContext from "../../../contexts/SelectedTeamContext";
import Theme from "../../../Theme";

const MAX_DESIGNATED_PITCHER = 10;
const MAX_FINAL_ROSTER_DESIGNATED_PITCHERS = 4;

const Header = styled.div`
  display: flex;
  align-items: center;
  height: 32px;
  width: 100%;
  padding-left: 12px;
  background-color: #f7f7f7;
  border-bottom: 1px solid ${props => props.theme["light-grey"]};

  font-size: 14px;
  font-weight: bold;
  font-family: Helvetica;
  font-style: normal;
  font-stretch: normal;
  letter-spacing: normal;
  color: ${props => props.theme["dark-grey"]};

  @supports (-moz-appearance: none) {
    line-height: 32px;
  }
`;

const List = styled.ul`
  margin: 0;
  padding-left: 0;
  list-style: none;
  counter-reset: item-counter;
`;

const PitcherSelector = styled(Select)`
  width: 90%;
  height: 36px;
  padding-left: 25px;
`;

const DesignatedPitchersTable = () => {
  const showAlert = useAlert();
  const { teamId: fedTeamId } = useContext(SelectedTeamContext).state.team;
  const rosterContext = useContext(RosterContext);
  const { availablePlayers, final } = rosterContext.state;
  const [designatedPitchersMap, setDesignatedPitchersMap] = useState(new Map());

  const pitcherToOption = pitcher => {
    return {
      value: pitcher.profileId,
      label: `${pitcher.lastName}, ${pitcher.rosterFirstName || pitcher.firstName}`
    };
  };

  const isPitcher = player => {
    return (
      player.position === "LHR" ||
      player.position === "LHS" ||
      player.position === "RHR" ||
      player.position === "RHS" ||
      player.position === "LHP" ||
      player.position === "RHP"
    );
  };

  useEffect(() => {
    if (availablePlayers) {
      const currentDesignatedPitchers = availablePlayers.filter(
        player => player.designatedPitcher && isPitcher(player)
      );
      const newMap = new Map();
      currentDesignatedPitchers.forEach((player, index) => {
        newMap.set(index + 1, player); // Use 1-based indexing
      });
      setDesignatedPitchersMap(newMap);
    }
  }, [availablePlayers.length]);

  const designatedPitcherOptions = useMemo(() => {
    const selectedPitcherIds = Array.from(designatedPitchersMap.values()).map(pitcher => pitcher.profileId);
    const availableOptions = availablePlayers.filter(
      player => !selectedPitcherIds.includes(player.profileId) && isPitcher(player)
    );
    return availableOptions.map(player => pitcherToOption(player));
  }, [availablePlayers, designatedPitchersMap]);

  const finalRosterPitchersInDesignatedPitchers = useMemo(() => {
    const finalPitchers = final?.pitchers?.map(pitcher => pitcher.profileId) || [];
    const designatedPitcherIds = Array.from(designatedPitchersMap.values()).map(pitcher => pitcher.profileId);
    return designatedPitcherIds.filter(pitcherId => finalPitchers.includes(pitcherId)).length;
  }, [final, designatedPitchersMap]);

  const handleSelectDesignatedPitcher = (profileId, positionIndex) => {
    const selectedPlayer = availablePlayers.find(player => player.profileId === profileId);
    const currentPlayer = designatedPitchersMap.get(positionIndex);

    if (!selectedPlayer && currentPlayer) {
      // Case 1: Removing a designated pitcher (clearing the position)
      RosterApi.updateDesignatedPitcher(fedTeamId, currentPlayer.profileId, false)
        .then(() => {
          setDesignatedPitchersMap(prev => {
            const newMap = new Map(prev);
            newMap.delete(positionIndex);
            return newMap;
          });
          showAlert("Designated Pitcher removed", "success");
        })
        .catch(error => {
          console.error(error);
          showAlert("Error removing designated pitcher", "danger");
        });
    } else if (selectedPlayer && (!currentPlayer || selectedPlayer.profileId !== currentPlayer.profileId)) {
      // Case 2 & 3: Adding or replacing a designated pitcher
      // Check if the selected player is on the final roster
      const finalPitchers = final?.pitchers?.map(pitcher => pitcher.profileId) || [];
      const isSelectedPlayerOnFinalRoster = finalPitchers.includes(selectedPlayer.profileId);
      // If we're trying to add a player from the final roster, check the limit
      if (
        isSelectedPlayerOnFinalRoster &&
        finalRosterPitchersInDesignatedPitchers >= MAX_FINAL_ROSTER_DESIGNATED_PITCHERS
      ) {
        showAlert("You can only select 4 designated pitchers from the final roster", AlertConstants.TYPES.WARNING);
        return;
      }
      let promiseChain = Promise.resolve();
      // Remove the current player if it exists
      if (currentPlayer) {
        promiseChain = RosterApi.updateDesignatedPitcher(fedTeamId, currentPlayer.profileId, false);
      }
      // Add the new player
      promiseChain
        .then(() => RosterApi.updateDesignatedPitcher(fedTeamId, selectedPlayer.profileId, true))
        .then(() => {
          setDesignatedPitchersMap(prev => {
            const newMap = new Map(prev);
            newMap.set(positionIndex, selectedPlayer);
            return newMap;
          });
          showAlert(`Designated Pitcher ${currentPlayer ? "updated" : "set"}`, "success");
        })
        .catch(error => {
          console.error(error);
          showAlert("Error updating designated pitcher", "danger");
        });
    }
  };

  return (
    <div>
      <Header>Designated Pitchers</Header>
      <List>
        {Array.from({ length: MAX_DESIGNATED_PITCHER }, (_, i) => i + 1).map(number => (
          <ListItem key={number} style={{ height: "42px" }}>
            <PitcherSelector
              name={`designatedPitcher${number}`}
              value={designatedPitchersMap.get(number) ? pitcherToOption(designatedPitchersMap.get(number)) : null}
              options={designatedPitcherOptions}
              onChange={option => handleSelectDesignatedPitcher(option?.value, number)}
              isClearable={true}
              styles={{
                control: base => ({
                  ...base,
                  backgroundColor:
                    finalRosterPitchersInDesignatedPitchers > MAX_FINAL_ROSTER_DESIGNATED_PITCHERS &&
                    !!designatedPitchersMap.get(number) &&
                    number > 4
                      ? Theme.danger.background
                      : "#fff"
                })
              }}
            />
          </ListItem>
        ))}
      </List>
    </div>
  );
};

export default DesignatedPitchersTable;
